import {
  array,
  excludes,
  literal,
  nonEmpty,
  nonNullable,
  nullable,
  number,
  object,
  pipe,
  string,
} from 'valibot';

import useTranslation from '@hooks/useTranslation';

import { selectAppPersistDowntimeType } from '@store/useAppStorePersist';

import { getSeverityLevelList } from '../helpers/getSeverityLevelList';

export const COMPLETION_REPORT_FIELDS = {
  TEXT: 'text',
  IS_SENSOR_ATTACHED: 'is_sensor_attached',
  ANY_REPAIRS_OR_MAINTENANCE: 'any_repairs_or_maintenance',
  NO_ACTIONS_SWITCHER: 'no_actions_switcher',
  IS_NO_ACTIONS: 'is_no_actions',
  FILE_NAME: 'file_name',
  CAUSE_CATEGORY_SEVERITIES: 'causeCategorySeverities',
  CAUSE: 'cause',
  DOWNTIME_AVOIDED: 'downtime_avoided',
  DOWNTIME_TYPE: 'downtime_type',
  LPH_AVOIDED: 'lph_avoided',
  LPH_ADDITIONAL_EXPLANATION: 'lph_additional_explanation',
  EQUIPMENT_LABOR_SAVINGS: 'equipment_labor_saving',
  NO_ACTIONS_GROUP_ID: 'no_actions_group_id',
  NO_ACTIONS_GROUP_OTHER_TEXT: 'no_actions_group_other_text',
  REPLACED_PARTS: 'replaced_parts',
};

export const schemaCRStepBasic = object({
  [COMPLETION_REPORT_FIELDS.TEXT]: pipe(string(), nonEmpty('This field is required.')),
  [COMPLETION_REPORT_FIELDS.IS_SENSOR_ATTACHED]: literal(true, 'Sensor should be attached.'),
  [COMPLETION_REPORT_FIELDS.ANY_REPAIRS_OR_MAINTENANCE]: pipe(string(), nonEmpty('This field is required.')),
  [COMPLETION_REPORT_FIELDS.IS_NO_ACTIONS]: nullable(number()),
  [COMPLETION_REPORT_FIELDS.FILE_NAME]: array(
    object({ path: string(), extension: string(), name: string(), size: nullable(number()), url: string() }),
  ),
});

export const schemaCRStepDetails = object({
  [COMPLETION_REPORT_FIELDS.FILE_NAME]: array(
    object({ path: string(), extension: string(), name: string(), size: nullable(number()), url: string() }),
  ),
  [COMPLETION_REPORT_FIELDS.CAUSE_CATEGORY_SEVERITIES]: pipe(
    array(
      object({
        severity: object({
          id: nonNullable(nullable(number()), 'This field is required.'),
        }),
        cause_category: object({
          value: nonNullable(nullable(string()), 'This field is required.'),
        }),
      }),
    ),
  ),
  [COMPLETION_REPORT_FIELDS.CAUSE]: pipe(string(), nonEmpty('This field is required.')),
});

export const schemaCRStepMetrics = (hasLphAvoided) => {
  let lphAvoidedField = {};
  if (hasLphAvoided) {
    lphAvoidedField = {
      [COMPLETION_REPORT_FIELDS.LPH_AVOIDED]: pipe(
        string(),
        excludes('-', 'The value cannot be negative.'),
        nonEmpty('This field is required'),
      ),
    };
  }

  return object({
    [COMPLETION_REPORT_FIELDS.DOWNTIME_AVOIDED]: pipe(
      string(),
      excludes('-', 'The value cannot be negative.'),
      nonEmpty('This field is required'),
    ),
    ...lphAvoidedField,
    [COMPLETION_REPORT_FIELDS.EQUIPMENT_LABOR_SAVINGS]: pipe(
      string(),
      excludes('-', 'The value cannot be negative.'),
    ),
    [COMPLETION_REPORT_FIELDS.DOWNTIME_TYPE]: number(),
  });
};

export const schemaCRStepReplaceParts = object({
  [COMPLETION_REPORT_FIELDS.REPLACED_PARTS]: pipe(
    array(object({ nameOfOem: string(), amazonPart: string(), amazonEamPart: string() })),
  ),
});

export const completionReportDefaultValues = (actionItem, report, hasTemplateCurrency, controls) => {
  const { t } = useTranslation();
  const userDowntimeType = selectAppPersistDowntimeType();

  const getAnyRepairsOrMaintenance = () => {
    if (!controls?.isReportSubmitted) {
      return '';
    }

    if (controls?.isWwAi) {
      return +actionItem.is_no_actions === 1 ? 'no_actions' : 'maintenance';
    }
    if (controls?.isNoActions) {
      return +actionItem.no_actions_type === 1 ? 'no_actions' : 'maintenance';
    }

    return 'yes';
  };

  const defaultCauseCategorySeverities = () => {
    if (report?.id && actionItem?.type === 'regular' && report?.causeCategorySeverities?.length) {
      const severityLevelList = getSeverityLevelList(t);

      return report?.causeCategorySeverities?.map((category) => {
        return {
          severity: severityLevelList.find((item) => +item.id === +category.severity),
          cause_category: { value: category.cause_category, label: t(category.cause_category) },
        };
      });
    }

    return [{ severity: { id: null }, cause_category: { value: null } }];
  };

  const filesList = report?.file?.map((file) => {
    return {
      ...file,
      url: file.path || '',
      size: file.size || null,
    };
  });

  const equipmentLaborSaving = report?.equipment_labor_saving_in_currency ?? report?.equipment_labor_saving;

  return {
    [COMPLETION_REPORT_FIELDS.TEXT]: controls?.isNoActions
      ? actionItem.no_actions_description
      : report?.text || '',
    [COMPLETION_REPORT_FIELDS.IS_SENSOR_ATTACHED]: controls?.isWwAi
      ? true
      : report?.id
      ? !!report?.is_sensor_attached || false
      : controls?.isNoActions
      ? !!actionItem.is_sensor_attached || false
      : null,
    [COMPLETION_REPORT_FIELDS.ANY_REPAIRS_OR_MAINTENANCE]: getAnyRepairsOrMaintenance(),
    [COMPLETION_REPORT_FIELDS.NO_ACTIONS_SWITCHER]: controls?.isReportSubmitted
      ? controls?.isNoActions
        ? '1'
        : '0'
      : null,
    [COMPLETION_REPORT_FIELDS.IS_NO_ACTIONS]:
      (controls?.isWwAi && controls?.isReportSubmitted) || controls?.isNoActions
        ? +actionItem.no_actions_type === 1
          ? (+!controls?.isWwAi).toString()
          : (+!!controls?.isWwAi).toString()
        : null,
    [COMPLETION_REPORT_FIELDS.FILE_NAME]: filesList || [],
    [COMPLETION_REPORT_FIELDS.CAUSE_CATEGORY_SEVERITIES]: defaultCauseCategorySeverities(),
    [COMPLETION_REPORT_FIELDS.CAUSE]: report?.cause || '',
    [COMPLETION_REPORT_FIELDS.DOWNTIME_AVOIDED]: (
      report?.downtime_avoided_calculated ||
      report?.downtime_avoided ||
      ''
    ).toString(),
    [COMPLETION_REPORT_FIELDS.DOWNTIME_TYPE]: report?.downtime_type ?? userDowntimeType,
    [COMPLETION_REPORT_FIELDS.EQUIPMENT_LABOR_SAVINGS]: (equipmentLaborSaving || '').toString(),
    [COMPLETION_REPORT_FIELDS.LPH_AVOIDED]: (report?.lph_avoided || '').toString(),
    [COMPLETION_REPORT_FIELDS.LPH_ADDITIONAL_EXPLANATION]: report?.lph_additional_explanation || '',
    [COMPLETION_REPORT_FIELDS.NO_ACTIONS_GROUP_ID]: actionItem?.no_actions_group_id || '',
    [COMPLETION_REPORT_FIELDS.NO_ACTIONS_GROUP_OTHER_TEXT]: actionItem?.no_actions_group_other_text || '',
    [COMPLETION_REPORT_FIELDS.REPLACED_PARTS]: report?.replaced_parts?.length
      ? report?.replaced_parts
      : [{ nameOfOem: '', amazonPart: '', amazonEamPart: '' }],
  };
};
